import {
  WorkflowDefinitionListItemDto,
  WorkflowDefinitionStatus,
} from "../../types/workflowDefinitionDto";
import { WORKFLOW_CANVAS_TYPES, WorkflowCanvasType } from "./workflowBuilderData";

/** One Library card, read off a `GET /api/workflow/definitions` row (Epic 80428). */
export interface WorkflowLibraryItem {
  /** The listed version — what Open hands the Builder. */
  id: number;
  /** The family — what Run starts from, so a case always lands on the latest published version. */
  rootDefinitionId: number;
  name: string;
  token: string | null;
  category: string;
  status: WorkflowDefinitionStatus;
  /** `null` hides the line — no description, or an API build that doesn't serve one yet. */
  description: string | null;
  version: number;
  stageCount: number;
  /** `null` = the neutral tile: a blank draft, or an API build that doesn't serve it yet. */
  startStageType: WorkflowCanvasType | null;
  /** `null` = unknown (an API build that predates the real count) — never shown as 0. */
  activeInstanceCount: number | null;
  /** `YYYY-MM-DD`, or `null` for a draft that has never been re-saved. */
  editedOn: string | null;
}

/** Shown, and filtered on, for a definition saved without a category. */
export const UNCATEGORIZED = "Uncategorized";

const CANVAS_TYPES = new Set<string>(WORKFLOW_CANVAS_TYPES.map((option) => option.type));

const toCanvasType = (stageType: string | null | undefined): WorkflowCanvasType | null =>
  stageType && CANVAS_TYPES.has(stageType) ? (stageType as WorkflowCanvasType) : null;

export const toWorkflowLibraryItem = (dto: WorkflowDefinitionListItemDto): WorkflowLibraryItem => {
  // `description` and `startStageType` shipped in the same API change as the real instance count,
  // so the key being absent means the count is still the old hard-coded 0 — unknown, not zero.
  const servesCardFields = dto.description !== undefined;

  return {
    id: dto.id,
    rootDefinitionId: dto.rootDefinitionId,
    name: dto.name,
    token: dto.token,
    category: dto.category?.trim() || UNCATEGORIZED,
    status: dto.status,
    description: dto.description?.trim() || null,
    version: dto.versionNo,
    stageCount: dto.stageCount,
    startStageType: toCanvasType(dto.startStageType),
    activeInstanceCount: servesCardFields ? dto.instanceCount : null,
    // Eastern wall-clock time from the API with no offset — take the date part as written rather
    // than parsing it, which would shift it into the browser's zone.
    editedOn: dto.updatedOn ? dto.updatedOn.slice(0, 10) : null,
  };
};

/**
 * The header's library-wide instance total — every loaded family, whatever the filters show.
 * `null` while any row's count is unknown, so the header falls back to definitions only rather
 * than reporting a partial sum.
 */
export const getLibraryInstanceTotal = (items: WorkflowLibraryItem[]): number | null =>
  items.reduce<number | null>(
    (total, item) =>
      total === null || item.activeInstanceCount === null ? null : total + item.activeInstanceCount,
    0,
  );

// Duplicating the same source twice must not produce two identically named drafts — the second
// copy becomes "Copy of X (2)". Compared ignoring case and surrounding spaces, so "copy of x"
// already in the library counts as taken, the way a case-insensitive database compare treats it.
export const buildCopyName = (sourceName: string, existing: WorkflowLibraryItem[]): string => {
  const normalise = (name: string) => name.trim().toLowerCase();
  const base = `Copy of ${sourceName}`;
  const taken = new Set(existing.map((item) => normalise(item.name)));
  const isTaken = (name: string) => taken.has(normalise(name));
  if (!isTaken(base)) {
    return base;
  }

  let suffix = 2;
  while (isTaken(`${base} (${suffix})`)) {
    suffix += 1;
  }
  return `${base} (${suffix})`;
};
